import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, ReplaySubject, switchMap } from "rxjs";
import {
  ActivityTypes,
  FetchClassificatorsService,
  GoalTypes,
  ObservationTypes,
  PeriodRepeats,
  StatusTypes,
  TaskTypes,
} from "../../services/fetch-classificators.service";
import { SessionContext } from "../../models/session-context.model";
import {
  BasePrescription,
  HealthPlanActivities,
  HealthPlanActivitiesReport,
  HealthPlanData,
  HealthPlanGoals,
  HealthPlanGoalTargets,
  HealthPlanTasks,
  MedicationRequest,
  MedicationStatement,
  OverTheCounterMedication,
} from "../../models/health-plan.model";
import { ObservationData } from "../../models/observation.model";
import { GeneralRequestService } from "../../services/general-request.service";
import { NgxMeasurementsService } from "../../services/ngx-measurements.service";
import { UserInfoService } from "../../services/user-info.service";
import { GeneralRequestResponse } from "../../models/payloads/shared/save-request.model";
import { map } from "rxjs/operators";
import { getISOWeek, getISOWeekYear, isAfter, subMinutes } from "date-fns";

class PreviousFetch {
  personId: number;
  time: Date;
}


@Injectable({
  providedIn: "root",
})
export class NgxHealthPlanService implements OnDestroy {

  public readonly FORM_RETAIN_ROUTES = [
    "/prescriptions",
    "/prescriptions/info",
    "/prescriptions/review",
    "/prescriptions/new",
    "/paid-prescriptions",
    "/paid-prescriptions/info",
    "/paid-prescriptions/review",
    "/paid-prescriptions/new",
    "/ward-info"
  ];

  userData: SessionContext;
  userHealthPlans: HealthPlanData[] = [];
  tasksHealthPlan: HealthPlanTasks[] = [];
  activitiesHealthPlan: HealthPlanActivities[] = [];
  goalsHealthPlan: HealthPlanGoals[] = [];
  goalTargetsHealthPlan: HealthPlanGoalTargets[] = [];
  activityReports: HealthPlanActivitiesReport[] = [];
  medication: any[];
  draftHealthPlanIds: number[] = [];
  draftHealthPlans: HealthPlanData[] = [];
  // subs
  tasksChanges = new BehaviorSubject<HealthPlanTasks[]>(this.tasksHealthPlan);
  healthPlanChanges = new BehaviorSubject<HealthPlanData[]>(this.userHealthPlans);
  healthPlanObs = this.healthPlanChanges.asObservable();
  activitiesChanges = new BehaviorSubject<HealthPlanActivities[]>(this.activitiesHealthPlan);
  targetsChanges = new BehaviorSubject<HealthPlanGoalTargets[]>(this.goalTargetsHealthPlan);
  activityReportChanges = new BehaviorSubject<HealthPlanActivitiesReport[]>(this.activityReports);
  goalsChanges = new BehaviorSubject<HealthPlanGoals[]>(this.goalsHealthPlan);
  healthPlanReady = new BehaviorSubject<boolean>(false);
  allMedications = new BehaviorSubject<BasePrescription[] | OverTheCounterMedication[]>([]);
  // When user clicks in medication to renew prescription store medication data to be used in prescription-list
  prescriptionToRenew = new ReplaySubject<BasePrescription>(1);
  activeHealthPlan: HealthPlanData;
  lastFitbitUpdateTime = new ReplaySubject<Date>(1);
  // motivationalQuestionnaire
  motivationalQuestionnaireChanges = new ReplaySubject<ObservationData>(1);
  latestMotivationalQuestionnaire: ObservationData;
  // periodicQuestionnaire
  periodicQuestionnaireChanges = new ReplaySubject<ObservationData>(1);
  latestPeriodicQuestionnaire: ObservationData;
  buildChartData = new BehaviorSubject<boolean>(false);
  private obsTypes: ObservationTypes;
  private healthPlanStatus: StatusTypes;
  private goalTypes: GoalTypes;
  private activityTypes: ActivityTypes;
  private medicationStatements: MedicationStatement[];
  private healthPlanMonitoredMeds: BasePrescription[];
  private prescribedMedications: BasePrescription[];
  private mispPrescribedMedications: BasePrescription[];
  private otcMedication: OverTheCounterMedication [];
  private periodRepeat: PeriodRepeats;
  private taskTypes: TaskTypes;
  private subs = [];
  private previousFetch: PreviousFetch[] = [];
  /**
   * Helper fetch method to get data for health plan versioning relations
   * Fetches recursively if return count exceeds limit and offset
   * @param offset
   * @param id what healthPlan relations you want?
   * @param personId for what person?
   * @param tableName e.g TaskHealthPlan
   * @param includeRelation e.g ['Task'] but this is optional
   */

  private responseBody: GeneralRequestResponse;
  private offset = 0;

  constructor(private generalRequestService: GeneralRequestService,
              private measurementsService: NgxMeasurementsService,
              private userService: UserInfoService,
              private classificatorService: FetchClassificatorsService,) {

    // type codes
    const obsTypesSub = this.classificatorService.observationTypes.subscribe((data) => this.obsTypes = data);
    const statusSub = this.classificatorService.statusTypes.subscribe((data) => this.healthPlanStatus = data);
    const goalsSub = this.classificatorService.goalTypes.subscribe((data) => this.goalTypes = data);
    const taskTypesSub = this.classificatorService.taskTypes.subscribe((data) => this.taskTypes = data);
    const activitiesTypeSub = this.classificatorService.activityTypes.subscribe((data) => this.activityTypes = data);
    const periodRepeatSub = this.classificatorService.repeatPeriods.subscribe((data) => this.periodRepeat = data);

    this.userData = this.userService.getSessionContext();
    const loadingStatusSub = this.generalRequestService.loadingDataStatus.subscribe((status) => {
      if (status.isHealthPlanDataLoading == false) {
        this.healthPlanReady.next(true);
      }
    });

    this.measurementsService.observationsArrayChanges.subscribe(() => this.addObservationsToActivity());

    this.subs.push(...[
      obsTypesSub,
      periodRepeatSub,
      statusSub,
      goalsSub,
      taskTypesSub,
      activitiesTypeSub,
      loadingStatusSub,
    ]);
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  // Fetch all user related Heath Plan (HP) data
  async fetchHealthPlan(personId: number) {
    if (!this.canUserFetchAgain(personId)) return;
    // Get user info from storage STAGE 0
    if (!personId) return;
    // Fetch HP STAGE 2
    await this.fetchHealthPlanData(personId);
    let activePlan: HealthPlanData = this.findActiveHealthPlan(personId);
    if (!activePlan) return;
    // Fetch HP Tasks STAGE 3
    await this.fetchHealthPlanTasks(activePlan.id, personId);
    // Fetch HP Activity STAGE 3
    await this.fetchHealthPlanActivity(activePlan.id, personId);
    // Fetch HP Goals STAGE 3
    await this.fetchHealthPlanGoals(activePlan.id, personId);
    // Fetch HP Targets STAGE 4
    await this.fetchHealthPlanTargets(personId);
    // Fetch HP Observations STAGE 4
    await this.fetchHealthPlanObservations(personId);
    // Fetch HP Activity Reports STAGE 4
    await this.fetchHealthPlanActivityReport(personId);

    let newFetch: PreviousFetch = {
      personId: personId,
      time: new Date(),
    };
    this.previousFetch.push(newFetch);
    this.generalRequestService.loadingStatus.isHealthPlanDataLoading = false;
    this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
    this.buildChartData.next(true);
  }

  canUserFetchAgain(personId: number): boolean {
    let answer = true;
    const previousFetches = this.previousFetch.filter(oldFetch => oldFetch.personId === personId);
    if (!previousFetches.length) return answer;
    // Sort by timestamp
    previousFetches.sort((a, b) => a.time.getTime() - b.time.getTime());
    const oldestFetchTime = previousFetches[0].time;
    const cutoffTime = subMinutes(new Date(), 30); // 30 minutes ago
    if (isAfter(oldestFetchTime, cutoffTime)) {
      answer = false;
    }
    return answer;
  }

  findActiveHealthPlan(personId: number) {
    return this.userHealthPlans.find(plan => plan.personId == personId && plan.statusCodeId == this.healthPlanStatus.ACTIVE);
  }

  fetchHealthPlanData(personId: number) {
    return this.generalRequestService.fetchHealthPlanData(personId).then((response) => {
      this.mergeItemsById(this.userHealthPlans, response.items);
      this.selectActiveHealthPlan();
      this.healthPlanChanges.next(this.userHealthPlans);
    });
  }

  // STAGE 4 Fetch Observations related to Goals and Activity
  fetchHealthPlanObservations(personId: number) {
    this.measurementsService.fetchAllObservations(personId);
  }

// STAGE 4 Fetch Activity Reports
  async fetchHealthPlanActivityReport(personId: number) {
    let activityIds: number[] = this.activitiesHealthPlan.filter(activity => activity.personId === personId).map(activity => activity.id);
    let reports = await this.healthPlanVersioningHelperFetch(0, [""], activityIds, personId, "ActivityReport.activityId");
    this.updateActivityReportsArray(reports, "add");
    this.addReportsToActivity(reports);
    this.activityReportChanges.next(this.activityReports);
  }

  // Returns observation types for goals
  getObservationTypesFromGoal(goals: HealthPlanGoals[]) {
    let obsTypes = [];
    for (let goal of goals) {
      if (goal.typeId == this.goalTypes.WEIGHT) {
        obsTypes.push(this.obsTypes.weight, this.obsTypes.height, this.obsTypes.waist, this.obsTypes.BMI);
      } else if (goal.typeId == this.goalTypes.BLOOD_PRESSURE) {
        obsTypes.push(this.obsTypes.pulse, this.obsTypes.diastolic, this.obsTypes.systolic, this.obsTypes.bloodPressure);
      } else if (goal.typeId == this.goalTypes.BLOOD_SUGAR) {
        obsTypes.push(this.obsTypes.bloodSugar);
      } else if (goal.typeId == this.goalTypes.PEF) {
        obsTypes.push(this.obsTypes.PEF);
      }
    }
    return obsTypes;
  }

  // Selects Active or Draft Health Plan to fetch data for
  getActiveOrDraftHP(): HealthPlanData {
    let active = this.userHealthPlans.find((healthPlan) => healthPlan.statusCodeId == this.healthPlanStatus.ACTIVE);
    let draft = this.userHealthPlans.find((healthPlan) => healthPlan.statusCodeId == this.healthPlanStatus.DRAFT);
    if (active) {
      return active;
    } else if (!active && draft) {
      return draft;
    }
  }

  // Create task from active or draft health plan and add it to TasksArray for calendar to read
  createTaskFromHealthPlan() {
    let activeHealthPlan = this.userHealthPlans.find((HP) => HP.statusCodeId == this.healthPlanStatus.ACTIVE);
    let draftHealthPlan = this.userHealthPlans.find((HP) => HP.statusCodeId == this.healthPlanStatus.DRAFT);
    let healthPlan = activeHealthPlan ? activeHealthPlan : draftHealthPlan;
    if (healthPlan) {
      let healthPlanPeriod = new HealthPlanTasks();
      healthPlanPeriod.periodEnd = healthPlan.periodEnd;
      healthPlanPeriod.periodStart = healthPlan.periodStart;
      healthPlanPeriod.typeId = 0;
      healthPlanPeriod.id = -1;
      healthPlanPeriod.visibleToPatient = true;
      this.updateTasksArray([healthPlanPeriod]);
      let healthPlanStart = new HealthPlanTasks();
      healthPlanStart.scheduled = healthPlan.periodStart;
      healthPlanStart.typeId = 1;
      healthPlanStart.id = -2;
      healthPlanStart.visibleToPatient = true;
      this.updateTasksArray([healthPlanStart]);
    } else {
      // console.log('ERROR. No ACTIVE or DRAFT Health Plan for user');
    }
  }

  getActiveHealthPlan(personId: number) {
    return this.userHealthPlans.find((healthPlan) => healthPlan.statusCodeId == this.healthPlanStatus.ACTIVE && healthPlan.personId == personId);
  }

  /**
   * Everything below is only for Patient Mobile
   */

  postActivityReport(report: HealthPlanActivitiesReport) {
    if (!report.personId) {
      report.personId = this.userService.getSessionContext().principal.id;
    }
    this.generalRequestService.postActivityReport(report).then((response) => {
      console.warn("response.items", response.items);
      this.updateActivityReportsArray(response.items, "add");
    });
  }

  deleteActivityReport(report: HealthPlanActivitiesReport) {
    this.generalRequestService.deleteActivityReport(report).then((response) => {
      console.warn("response.items", response.items);
      this.updateActivityReportsArray(response.items, "delete");
    });
  }

  updateRepeatFrequency(activity: HealthPlanActivities, button: number, selectedWeek: Date) {

    // Find existing report to edit
    let selectedWeekReport: HealthPlanActivitiesReport = returnWeekItems(activity.reportsList, selectedWeek)[0];

    // If there isn't one, must be first time reporting
    if (!selectedWeekReport) {
      selectedWeekReport = new HealthPlanActivitiesReport();
      selectedWeekReport.repeatValue = 1;
      selectedWeekReport.activityId = activity.id;
    } else {
      if (button > 0) {
        selectedWeekReport.repeatValue = selectedWeekReport.repeatValue - 1;
      } else if (button == 0) {
        selectedWeekReport.repeatValue = selectedWeekReport.repeatValue + 1;
      }
    }
    selectedWeekReport.effectiveTime = selectedWeek;
    this.postActivityReport(selectedWeekReport);
  }

  // Fetches and processes medication data for a user's active health plan.
  async fetchMedicationData() {
    const sessionContext = this.userService.getSessionContext();
    const personId = sessionContext.principal.id;
    const personalCode = sessionContext.principal.personalCode;

    if (!personId) return;

    const activePlan = this.findActiveHealthPlan(personId);
    if (!activePlan) return;

    this.generalRequestService.loadingStatus.isMedicationDataLoading = true;
    this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);

    try {
      await this.fetchMedicationStatementData(personId, activePlan.id);
      this.healthPlanMonitoredMeds = await this.fetchPrescribedMedicationData();
      // Convert the Observable to a Promise and wait for it
      const medicationsWithinLastYear = await this.updateMedicationDataFromMisp(personalCode).pipe(
        switchMap((mispMedicationsResolved) => {
          return this.findCorrespondingMedications(this.healthPlanMonitoredMeds, of (mispMedicationsResolved));
        }),
        map((correspondingMeds) => {
          return this.filterMedicationsWithinLastYear(correspondingMeds);
        })
      ).toPromise();

      await this.fetchNonPrescribedMedicationData();
      this.setMedicationData(medicationsWithinLastYear);

      return {
        medicationsWithinLastYear,
        regularMedications: this.otcMedication
      }

    } catch (error) {
      console.error('Error fetching medication data:', error);
    } finally {
      this.generalRequestService.loadingStatus.isMedicationDataLoading = false;
      this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
    }
  }

  // Fetch user motivational questionnaires in past year
  fetchMotivationalQuestionnaire() {
    let personId = this.userService.getSessionContext().principal.id;
    const questionnaireClassificatorCode = JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_MOTIVATION_TEST"));
    this.generalRequestService.fetchMotivationalQuestionnaire(personId, questionnaireClassificatorCode).then((response) => {
      // console.log('latest questionnaire: ', [response.items]);
      if (response.items.length > 0) {
        this.latestMotivationalQuestionnaire = response.items[0];
        this.motivationalQuestionnaireChanges.next(this.latestMotivationalQuestionnaire);
      }
    });
  }

  // Fetch user periodic (quarterly) questionnaires in past year
  fetchPeriodicQuestionnaire() {
    let personId = this.userService.getSessionContext().principal.id;
    const questionnaireClassificatorCode = JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_FEEDBACK"));
    this.generalRequestService.fetchPeriodicQuestionnaire(personId, questionnaireClassificatorCode).then((response) => {
      // console.log('latest periodic questionnaire: ', [response.items]);
      if (response.items.length > 0) {
        this.latestPeriodicQuestionnaire = response.items[0];
      }
      this.periodicQuestionnaireChanges.next(this.latestMotivationalQuestionnaire);
    });
  }

  private async healthPlanVersioningHelperFetch(offset: number, includeRelation: string[], id: number[], personId: number, tableName: string) {
    const limit = 100;
    if (!offset) offset = 0;
    if (!includeRelation) includeRelation = [""];
    if (!id) return;
    if (!personId) return;
    if (!tableName) return;
    if (!tableName.includes(".")) tableName = tableName + ".healthPlanId";

    const keysToGet = ["taskId", "goalId", "statement", "contactId", "activityId"];
    let response = await this.generalRequestService.fetchHealthPlanRelation(offset, includeRelation, id, personId, tableName);
    if (response && response.pagination && response.items) {
      this.responseBody = response;
      // If response has more items than limit, then raise limit by offset and fetch again
      if (response.pagination.count > (limit + this.offset)) {
        this.offset += 100;
        await this.healthPlanVersioningHelperFetch(offset, includeRelation, id, personId, tableName);
      } else {
        let returnItems = [];
        let relationObjs = [];


        for (let item of this.responseBody.items) {
          for (let [k, v] of Object.entries(item)) {
            if (keysToGet.includes(k)) {
              returnItems.push(item);
            }
            if (v !== null && typeof v === "object") {
              relationObjs.push(v);
            }
          }
        }
        this.responseBody = null;

        // If include relation was given and we got objs, we probably want those
        if (relationObjs.length > 0) {
          return relationObjs;
        } else {
          // Otherwise we get just get the items we asked for, we can then use these id's to fetch from related tables
          return returnItems;
        }
      }
      return;
    }
    return;
  }

  // STAGE 3 Fetch HP Tasks
  private async fetchHealthPlanTasks(healthPlanId: number, personId: number) {
    let tasks = await this.healthPlanVersioningHelperFetch(0, ["Task"], [healthPlanId], personId, "TaskHealthPlan");
    this.createTaskFromHealthPlan();
    this.updateTasksArray(tasks);
    this.mergeItemsById(this.tasksHealthPlan, tasks);
    this.tasksChanges.next(this.tasksHealthPlan);
    return;
  }

  // STAGE 3 Fetch Activity
  private async fetchHealthPlanActivity(healthPlanId: number, personId: number) {
    let activities = await this.healthPlanVersioningHelperFetch(0, ["Activity"], [healthPlanId], personId, "ActivityHealthPlan");
    this.mergeItemsById(this.activitiesHealthPlan, activities);
    this.activitiesChanges.next(this.activitiesHealthPlan);
  }

  // STAGE 3 Fetch Goals
  private async fetchHealthPlanGoals(healthPlanId: number, personId: number) {
    let goals = await this.healthPlanVersioningHelperFetch(0, ["Goal"], [healthPlanId], personId, "GoalHealthPlan");
    this.mergeItemsById(this.goalsHealthPlan, goals);
    this.goalsChanges.next(this.goalsHealthPlan);
  }

  // STAGE 4 Fetch Targets related to Goals
  private async fetchHealthPlanTargets(personId: number) {
    let goalIds: number[] = this.goalsHealthPlan.filter(goal => goal.personId === personId).map(goal => goal.id);
    let targets = await this.healthPlanVersioningHelperFetch(0, [""], goalIds, personId, "Target.goalId");
    this.mergeItemsById(this.goalTargetsHealthPlan, targets);
    this.addTargetsToGoals(personId);
    this.targetsChanges.next(this.goalTargetsHealthPlan);
  }

  private addReportsToActivity(items: HealthPlanActivitiesReport[]) {
    if (items.length === 0) {
      return;
    }

    for (let item of items) {
      if (this.activitiesHealthPlan.some(activity => activity.id === item.activityId)) {
        let activity = this.activitiesHealthPlan.find(activity => activity.id === item.activityId);
        if (!activity.hasOwnProperty("reportsList")) Object.assign(activity, {reportsList: []});
        mergeByProperty(activity.reportsList, [item], "id");
      }
    }
  }

  private addObservationsToActivity() {
    for (let activity of this.activitiesHealthPlan) {
      if ([this.activityTypes.PHYSICAL_HIGH, this.activityTypes.PHYSICAL_LOW].includes(activity.typeId)) {

        if ([this.periodRepeat.CONTINUOUS, this.periodRepeat.NO_REPEAT].includes(activity.repeatPeriodId)) {
          return;
        }

        let matchingObs = this.measurementsService.observationArray.filter((obs) => {
          if (activity.personId !== obs.personId) return false;
          return [
            this.obsTypes.movementActivity,
            this.obsTypes.movement_LIGHTLY,
            this.obsTypes.movement_MODERATELY,
            this.obsTypes.movement_VERY,
          ].includes(obs.classificatorCodeId);
        });
        if (!activity.hasOwnProperty("observationList")) Object.assign(activity, {observationList: []});
        mergeByProperty(activity.observationList, matchingObs, "id");
      }
    }
  }

  // Pushes new tasks to Array for calendar to read
  private updateTasksArray(items: HealthPlanTasks[]) {
    for (let newTask of items) {
      if (!this.tasksHealthPlan.some((task) => task.id == newTask.id)) {
        this.tasksHealthPlan.push(newTask);
      }
    }
  }

  private mergeItemsById(originals: any[], newItems: any[]) {
    for (let newItem of newItems) {
      if (newItem.id) {
        if (!originals.some((item) => item.id == newItem.id)) {
          originals.push(newItem);
        }
      }
    }
  }

  // Adds Targets as children to associated Goals
  private addTargetsToGoals(personId: number) {
    const goals = this.goalsHealthPlan.filter(goal => goal.personId == personId);
    const targets = this.goalTargetsHealthPlan.filter(target => target.personId == personId);
    for (let goal of goals) {
      for (let target of targets) {
        if (goal.id == target.goalId) {
          if (!goal.targets) {
            goal.targets = [];
          }
          if (goal.targets.length === 0) {
            goal.targets.push(target);
          } else {
            if (!goal.targets.some(oldTarget => oldTarget.id == target.id)) {
              goal.targets.push(target);
            }
          }
        }
      }
      if (goal.targets) {
        goal.targets = goal.targets.sort((a, b) => a.measureId - b.measureId);
      }
    }

    // Sort to keep motivational goal at index [0]
    this.goalsHealthPlan.sort((a, b) => (a.typeId > b.typeId) ? 1 : -1);
    this.goalsHealthPlan = this.goalsHealthPlan.filter((goal) => goal.targets);
  }

  private updateActivityReportsArray(items: HealthPlanActivitiesReport[], mode: string) {
    let activityReports = this.activityReports;
    if (mode == "add") {

      for (let newReport of items) {
        if (!activityReports.some((oldReports) => oldReports.id == newReport.id)) {
          activityReports.push(newReport);
        } else {
          // Remove old and add new if is update
          let oldReport = activityReports.find((oldReport) => oldReport.id == newReport.id);
          if (!oldReport) return;
          activityReports.splice(activityReports.indexOf(oldReport), 1, newReport);
        }
      }
    } else if (mode == "delete") {
      for (let deleteReport of items) {
        let toDelete = activityReports.find((oldReport) => oldReport.id == deleteReport.id);
        activityReports.splice(activityReports.indexOf(toDelete), 1);
      }
    }
    this.activityReports = [...activityReports];
    this.activityReportChanges.next(this.activityReports);
    this.activitiesChanges.next([...this.activitiesHealthPlan]);
  }

  private selectActiveHealthPlan() {
    let activeHealthPlan = this.userHealthPlans.find((healthPlan) => healthPlan.statusCodeId == this.healthPlanStatus.ACTIVE);
    let draftHealthPlans: HealthPlanData[] = [];
    this.userHealthPlans.map((healthPlan) => {
      if (healthPlan.statusCodeId == this.healthPlanStatus.DRAFT) {
        draftHealthPlans.push(healthPlan);
        return healthPlan.id;
      }
    });
    if (activeHealthPlan) {
      this.activeHealthPlan = activeHealthPlan;
      // console.log('Found active health plans: ', activeHealthPlan);
    }
    if (draftHealthPlans.length > 0) {
      this.draftHealthPlans = draftHealthPlans;
    }
  }

  private async fetchMedicationStatementData(personId: number, healthPlanId: number) {
    this.medicationStatements = await this.healthPlanVersioningHelperFetch(
      0,
      [],
      [healthPlanId],
      personId,
      "MedicationStatementHealthPlan",
    );
  }

  // Fetches prescribed medication data based on medication statements.
  private async fetchPrescribedMedicationData() {
    if (!this.medicationStatements) return [];

    const healthPlanMonitoredMeds = this.medicationStatements
      .map((statement) => parseInt(statement.derivedFrom))
      .filter((item) => !isNaN(item));

    if (healthPlanMonitoredMeds.length === 0) return [];

    try {
      const response = await this.generalRequestService.fetchMedicationRequestData(healthPlanMonitoredMeds);
      this.prescribedMedications = response.items;
      return this.prescribedMedications;
    } catch (error) {
      return [];
    }
  }

  updateMedicationDataFromMisp(personalCode: string): Observable<BasePrescription[]> {
    return this.generalRequestService.updateMedicationDataFromMisp(personalCode).pipe(
      map((response: any) => {
        this.mispPrescribedMedications = response?.groupedItems || [];
        this.generalRequestService.loadingStatus.isMedicationDataLoading = false;
        return this.mispPrescribedMedications;
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }

  private async fetchNonPrescribedMedicationData() {
    const otcMedication = this.medicationStatements
      .map((statement) => parseInt(statement.medicationId))
      .filter((item) => !isNaN(item));

    if (otcMedication.length === 0) return;

    const response = await this.generalRequestService.fetchRegularMedicationData(otcMedication);
    this.otcMedication = response.items;
  }

  // Filters and returns medications that are prescribed and also found in mispMedications.
  private findCorrespondingMedications(
    healthPlanMonitoredMeds: any,
    mispMedications$: Observable<BasePrescription[]>
  ): Observable<BasePrescription[]> {
    if (!healthPlanMonitoredMeds) {
      console.info("Did not find corresponding medications!");
      return of([]);
    }

    return mispMedications$.pipe(
      map((mispMedications) =>
        mispMedications.filter(mispMedication =>
          healthPlanMonitoredMeds.some(med =>
            med.cureAtcCode?.trim().toUpperCase() === mispMedication.cureAtcCode?.trim().toUpperCase()
          )
        )
      )
    );
  }

  // Filters and returns medications with an authoredOnDate within the last year.
  private filterMedicationsWithinLastYear(medications) {

    if (!Array.isArray(medications)) {
      return [];
    }

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    return medications.filter((medication) => {
      const authoredOnDate = new Date(medication.authoredOnDate);
      return authoredOnDate >= oneYearAgo;
    });
  }

  private setMedicationData(medications) {

    if (this.otcMedication && this.otcMedication.length > 0) {
      medications.push(...this.otcMedication.slice());
    }
    this.allMedications.next(medications);
  }

}


// Returns items that are in current year and current week
export function returnWeekItems(items: any[], selectedWeek: Date): any[] {
  if (!items || !items.length) return [];

  const selectedWeekNum = getISOWeek(selectedWeek);
  const selectedYear = getISOWeekYear(selectedWeek);

  return items.filter((item) => {
    const itemDate = new Date(item.effectiveTime);
    return (
      getISOWeek(itemDate) === selectedWeekNum &&
      getISOWeekYear(itemDate) === selectedYear
    );
  });
}

export function mergeByProperty(target, source, prop) {
  source.forEach(sourceElement => {
    let targetElement = target.find(targetElement => {
      return sourceElement[prop] === targetElement[prop];
    });
    targetElement ? Object.assign(targetElement, sourceElement) : target.push(sourceElement);
  });
}
